// React and Router
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

// Form and Validation
import { Field, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

// Icons
import { IoArrowBackOutline } from 'react-icons/io5'

// Components
import Input from '../../components/common/CustomInput'
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

const UserUpdate = () => {
  // Hooks
  const navigate = useNavigate()
  const goBack = useGoBack()
  const { id } = useParams()
  const userPermissions = useUserStore((state) => state.user) || []

  // State
  const [isLoading, setLoading] = useState(true)
  const [roleList, setRoleList] = useState([])
  const [userData, setUserData] = useState({ email: '', role: '' })
  const [error, setError] = useState('')

  // Validation schema for form
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email address is required'),
    role: Yup.string().required('Role selection is required'),
  })

  // Fetch roles and user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch roles
        const rolesResponse = await axiosInterceptorInstance.get('/roles')
        if (rolesResponse.data.roles.length > 0) {
          const selectOptions = rolesResponse.data.roles.map((role) => ({
            label: role.name,
            value: role._id,
          }))
          setRoleList(selectOptions)
        }

        // Fetch user details
        const userResponse = await axiosInterceptorInstance.get(
          `/users/user/${id}`,
        )
        const user = userResponse.data
        setUserData({
          email: user?.email || '',
          role: user?.role?._id || '',
        })
      } catch (error) {
        CustomToast({
          message: 'Failed to fetch user data. Please try again.',
          type: 'error',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  // Formik configuration
  const formik = useFormik({
    initialValues: userData,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (!userPermissions.includes('user:update')) {
        CustomToast({
          message: 'You do not have permission to update users',
          type: 'error',
        })
        return
      }

      try {
        await axiosInterceptorInstance.put(`/users/user/${id}`, values)
        CustomToast({
          message: 'User updated successfully',
          type: 'success',
        })
        navigate('/user')
      } catch (error) {
        const errorMessage =
          error.response?.status === 409
            ? 'This email address is already registered'
            : 'Failed to update user. Please try again.'

        setError(errorMessage)
        CustomToast({
          message: errorMessage,
          type: 'error',
        })
      }
    },
  })

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <p className='text-lg text-gray-600'>Loading user details...</p>
      </div>
    )
  }

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={goBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Update User</h1>
          </div>
        </div>
      </PageHeader>

      <FormikProvider value={formik}>
        <form
          className='w-full sm:w-[500px] mx-auto'
          onSubmit={formik.handleSubmit}
          noValidate
        >
          {/* Email Field */}
          <div className='mb-4'>
            <Field
              name='email'
              type='email'
              label='Email Address'
              placeholder='Enter email address'
              component={Input}
              disabled
              autoComplete='email'
            />
          </div>

          {/* Role Selection */}
          <div className='my-6'>
            <label className='block font-medium mb-2' htmlFor='role'>
              Select Role:
            </label>
            <select
              id='role'
              className='w-full border-[2px] border-light-gray focus:border-prime rounded-lg p-3 focus:outline-none focus:border-orange invalid:border-red-700'
              name='role'
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value='' disabled>
                Select a Role
              </option>
              {roleList.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {formik.touched.role && formik.errors.role && (
              <div className='text-red-700 text-sm mt-1'>
                {formik.errors.role}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className='text-red-700 text-sm my-4 font-normal'>{error}</div>
          )}

          {/* Submit Button */}
          <button
            type='submit'
            disabled={formik.isSubmitting}
            className='w-full py-3 rounded-md sm:w-[500px] text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-auto disabled:bg-opacity-80 disabled:text-white'
          >
            {formik.isSubmitting ? 'Updating...' : 'Update User'}
          </button>
        </form>
      </FormikProvider>
    </div>
  )
}

export default UserUpdate
